import { Controller } from "@hotwired/stimulus";
import * as Sentry from "@sentry/browser";

export default class extends Controller {
  static values = {
    locale: {
      type: String,
      default: "en",
    },
    customAttributes: {
      type: Object,
      default: {},
    },
    user: String,
    automaticallyOpen: {
      type: Boolean,
      default: false,
    },
    automaticallyOpenPath: String,
    proactiveInvitation: String,
  };

  connect() {
    this.#setupScriptTag();
    this.setupOnLoadCallback();

    document.addEventListener("turbo:load", this.#init);
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.#init);
  }

  setAttributes(event) {
    if (!event.detail) return;

    const { key, title, content } = event.detail;

    try {
      window.jivo_api.setCustomData([{ key, title, content }]);
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  #setupScriptTag = () => {
    const script = document.createElement("script");
    script.src = "https://code.jivosite.com/widget/SfcMRRPQMj";
    script.async = true;
    document.head.appendChild(script);
  };

  setupOnLoadCallback = () => {
    window.jivo_onLoadCallback = () => {
      if (
        this.hasCustomAttributesValue &&
        Object.keys(this.customAttributesValue).length > 0
      ) {
        window.jivo_api.setContactInfo({
          email: this.customAttributesValue.employee_email,
          name: this.customAttributesValue.employee_name,
          phone:
            this.customAttributesValue.employee_phone ||
            this.customAttributesValue.employee_whatsapp_phone,
        });

        const customData = [];

        if (this.customAttributesValue.business_id) {
          customData.push({
            key: "business_id",
            title: "Business ID",
            content: this.customAttributesValue.business_id,
          });
        }

        if (this.customAttributesValue.business_name) {
          customData.push({
            key: "business_name",
            title: "Business Name",
            content: this.customAttributesValue.business_name,
          });
        }

        if (this.customAttributesValue.employee_id) {
          customData.push({
            key: "employee_id",
            title: "Employee ID",
            content: this.customAttributesValue.employee_id,
          });
        }

        if (this.customAttributesValue.employee_name) {
          customData.push({
            key: "employee_name",
            title: "Employee Name",
            content: this.customAttributesValue.employee_name,
          });
        }

        if (this.customAttributesValue.employee_email) {
          customData.push({
            key: "employee_email",
            title: "Employee Email",
            content: this.customAttributesValue.employee_email,
          });
        }

        if (this.customAttributesValue.employee_phone) {
          customData.push({
            key: "employee_phone",
            title: "Employee Phone",
            content: this.customAttributesValue.employee_phone,
          });
        }

        if (this.customAttributesValue.employee_whatsapp_phone) {
          customData.push({
            key: "employee_whatsapp_phone",
            title: "Employee WhatsApp Phone",
            content: this.customAttributesValue.employee_whatsapp_phone,
          });
        }

        if (customData.length > 0) {
          window.jivo_api.setCustomData(customData);
        }
      }

      if (this.automaticallyOpenValue && this.proactiveInvitationValue) {
        if (this.automaticallyOpenPathValue) {
          if (window.location.pathname === this.automaticallyOpenPathValue) {
            this.#setProactiveInvitationTimeout();
          }
        } else {
          this.#setProactiveInvitationTimeout();
        }
      }

      window.jivo_onLoadCallback = null;
    };
  };

  #setProactiveInvitationTimeout = () => {
    setTimeout(() => {
      window.jivo_api.showProactiveInvitation(this.proactiveInvitationValue);
    }, 1000);
  };

  #init = () => {
    window.jivo_init && window.jivo_init();
  };

  // Use this when we logout the user
  refreshSession = () => {
    window.jivo_onLoadCallback = () => {
      window.jivo_api.clearHistory();
      window.jivo_onLoadCallback = null;
    };
  };
}
